import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
// import { authService } from "../services/auth.service"; // Removed
import { useAuth } from "../hooks/useAuth"; // Added
import { socketService } from "../services/socket.service";
import { notificationService, Notification } from "../services/notification.service";
export type { Notification };
// import { useToast } from "../components/ui/use-toast"; // Removed

interface NotificationContextType {
    notifications: Notification[];
    unreadCount: number;
    loading: boolean;
    fetchNotifications: () => Promise<void>;
    markAsRead: (id: string) => Promise<void>;
    markAllAsRead: () => Promise<void>;
    deleteNotification: (id: string) => Promise<void>;
    toasts: Notification[];
    removeToast: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [toasts, setToasts] = useState<Notification[]>([]);

    // Use useAuth to get the user
    const { user } = useAuth();

    const fetchNotifications = useCallback(async () => {
        if (!user) return;

        try {
            setLoading(true);
            const [notifsResponse, count] = await Promise.all([
                notificationService.getNotifications(1, 20), // Get first page
                notificationService.getUnreadCount()
            ]);

            setNotifications(notifsResponse.items || []);
            setUnreadCount(count);
        } catch (error) {
            console.error("Failed to fetch notifications:", error);
        } finally {
            setLoading(false);
        }
    }, [user]);

    const markAsRead = async (id: string) => {
        try {
            const success = await notificationService.markAsRead(id);
            if (success) {
                setNotifications(prev =>
                    prev.map(n => n._id === id ? { ...n, read: true } : n)
                );
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (error) {
            console.error("Failed to mark notification as read:", error);
        }
    };

    const markAllAsRead = async () => {
        try {
            const success = await notificationService.markAllAsRead();
            if (success) {
                setNotifications(prev => prev.map(n => ({ ...n, read: true })));
                setUnreadCount(0);
            }
        } catch (error) {
            console.error("Failed to mark all as read:", error);
        }
    };

    const deleteNotification = async (id: string) => {
        try {
            const success = await notificationService.deleteNotification(id);
            if (success) {
                setNotifications(prev => prev.filter(n => n._id !== id));
                // If the deleted one was unread, decrement count? 
                // Logic might be complex if we don't know if it was read or not from just ID 
                // But usually we delete after reading. 
                // For correctness, we might want to re-fetch unread count.
                fetchNotifications();
            }
        } catch (error) {
            console.error("Failed to delete notification:", error);
        }
    };

    const removeToast = useCallback((id: string) => {
        setToasts(prev => prev.filter(t => t._id !== id));
    }, []);

    useEffect(() => {
        if (user) {
            // 1. Initial Fetch
            fetchNotifications();

            // 2. Connect Socket
            console.log("Connecting socket for user:", user.id);
            socketService.connect();

            const onConnect = () => {
                console.log("Socket connected, joining user room:", user.id);
                socketService.joinUserRoom(user.id);
            };

            socketService.on("connect", onConnect);

            // If already connected (re-render), join immediately
            if (socketService.isConnected()) {
                onConnect();
            }

            // 3. Listen for notifications
            const handleNotification = (newNotification: Notification) => {
                console.log("Frontend received notification:", newNotification);

                setNotifications(prev => [newNotification, ...prev]);
                setUnreadCount(prev => prev + 1);

                // Add to toasts for popup
                setToasts(prev => [...prev, newNotification]);

                // Auto-remove toast after 5 seconds
                setTimeout(() => {
                    removeToast(newNotification._id);
                }, 5000);
            };

            socketService.on("notification", handleNotification);

            return () => {
                console.log("Cleaning up NotificationContext effect");
                socketService.off("connect", onConnect);
                socketService.off("notification", handleNotification);
                socketService.disconnect();
            };
        }
    }, [user, fetchNotifications, removeToast]);

    return (
        <NotificationContext.Provider value={{
            notifications,
            unreadCount,
            loading,
            fetchNotifications,
            markAsRead,
            markAllAsRead,
            deleteNotification,
            toasts,
            removeToast
        }}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (context === undefined) {
        throw new Error("useNotifications must be used within a NotificationProvider");
    }
    return context;
};
